import React, { useState, useEffect, useRef } from 'react';
import {
    Home, Search, PlusSquare, Clapperboard, UserCircle,
    Send, Camera, Phone, Video, Info, ChevronLeft,
    Heart, MessageCircle, Bookmark, Share2, MoreHorizontal,
    Edit, Mic, Image as ImageIcon, Smile, Battery, Wifi, Signal,
    CheckCircle, AlertTriangle, X, BookOpen
} from 'lucide-react';

// --- MOCK DATA & CONSTANTS ---

const REALISTIC_USERS = {
    me: {
        username: 'alex.creative_studio',
        name: 'Alex Design',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=faces',
        story: true
    },
    attacker: {
        username: 'meta_security_center_verified', // Classic phishing username
        name: 'Meta Support Team',
        avatar: 'https://ui-avatars.com/api/?name=Meta+Support&background=0D8ABC&color=fff&size=150', // Looks official-ish
        verified: true // The fake verified badge trick
    },
    feed: [
        {
            username: 'zara_travels',
            location: 'Kyoto, Japan',
            avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop&crop=faces',
            image: 'https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=800&h=800&fit=crop',
            likes: '14,203',
            caption: 'Missing these autumn colors! 🍁 #travel #japan',
            time: '2 hours ago'
        }
    ],
    stories: [
        { username: 'tech_insider', avatar: 'https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=150&h=150&fit=crop&crop=faces' },
        { username: 'coffee_daily', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&h=150&fit=crop&crop=faces' },
        { username: 'sarah_fitness', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=faces' },
        { username: 'jason_photo', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces' }
    ],
    dms: [
        { username: 'jessica_m', msg: 'See you tomorrow! 👋', time: '14m', avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=faces', unread: true },
        { username: 'david_workout', msg: 'Sent a reel by gymfails', time: '2h', avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=faces', unread: false },
        { username: 'mom_home', msg: 'Call me when you are free', time: '5h', avatar: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?w=150&h=150&fit=crop&crop=faces', unread: false },
        { username: 'group_trip_2025', msg: 'Mike: I booked the flights!', time: '1d', avatar: 'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=150&h=150&fit=crop&crop=faces', unread: false },
    ]
};

const SPEAKER_AVATARS = {
    'sophia_travels_x': 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=150&h=150&fit=crop&crop=faces', // Attractive travel influencer vibe
    'crypto_king_01': 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=faces', // Crypto bro / Suit
    'luxe_jewelry_official_xx': 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=150&h=150&fit=crop&crop=faces', // Jewelry brand
    'iamjason': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=faces',
    'Meta Support Team': 'https://ui-avatars.com/api/?name=Meta+Support&background=0D8ABC&color=fff&size=150', // Official looking
    'default': 'https://ui-avatars.com/api/?name=User&background=random'
};

// Default scenario fallback
const getDefaultScenario = () => ({
    title: 'Instagram Copyright Phishing',
    description: "You receive a DM claiming copyright violation.",
    initialSceneId: 'start',
    scenes: {
        'start': {
            speaker: 'Meta Support Team',
            line: "⚠️ 𝗖𝗼𝗽𝘆𝗿𝗶𝗴𝗵𝘁 𝗩𝗶𝗼𝗹𝗮𝘁𝗶𝗼𝗻 𝗡𝗼𝘁𝗶𝗰𝗲 \n\nHello user, our system has detected an unusual login attempt and a copyright violation on your latest post. Your account is scheduled for suspension in 24 hours.",
            choices: [
                { text: "Suspension? How do I stop this?", nextSceneId: 'fail_panic' },
                { text: "This message looks fake. Who is this?", nextSceneId: 'correct_verify' }
            ]
        },
        'fail_panic': {
            speaker: 'Meta Support Team',
            line: "To cancel the suspension, verify your account ownership immediately at: \n\n🔗 www.meta-help-secure-login.com/verify \n\nFailure to verify will result in permanent deletion.",
            isEndState: true,
            isCorrect: false,
            explanation: "You fell for the phishing link. Official Instagram/Meta support will NEVER DM you asking you to click a link to verify your account. Violations appear in Settings > Account Status.",
            indicators: ["Urgency (24 hours)", "Suspicious Link (meta-help-secure...)", "Generic Greeting"]
        },
        'correct_verify': {
            speaker: 'Meta Support Team',
            line: "This is an automated system message. Do not reply. Your account will be deleted.",
            isEndState: true,
            isCorrect: true,
            explanation: "Great job! You noticed the suspicious nature of the DM. Real support tickets are handled via email or the in-app Support Inbox, not Direct Messages.",
            indicators: ["Bot-like behavior", "Threats of deletion"]
        }
    }
});

const InstagramSimulator = ({ scenarios = [], onComplete, challenge }) => {
    const [scenario, setScenario] = useState(null);
    const [history, setHistory] = useState([]);
    const [currentScene, setCurrentScene] = useState(null);
    const [isComplete, setIsComplete] = useState(false);
    const [view, setView] = useState('feed'); // Start in Feed view
    const [currentTime, setCurrentTime] = useState('');
    const [finalFeedback, setFinalFeedback] = useState(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [showInitialPopup, setShowInitialPopup] = useState(true);
    const handleCloseInitialPopup = () => {
        setShowInitialPopup(false);
    };

    const chatEndRef = useRef(null);

    // Clock effect
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            setCurrentTime(now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
        };
        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, []);

    // Initialize Scenario
    useEffect(() => {
        let finalScenario = null;
        if (challenge && challenge.steps && challenge.steps.length > 0) {
            const assembledScenes = {};
            let firstSceneId = null;
            const sortedSteps = [...challenge.steps].sort((a, b) => a.order_index - b.order_index);

            sortedSteps.forEach((step, index) => {
                let content = step.content;
                try { if (typeof content === 'string') content = JSON.parse(content); } catch (e) { }
                const sceneKey = content.sceneId || step.title || step.step_id;
                if (index === 0) firstSceneId = sceneKey;
                assembledScenes[sceneKey] = { ...content, choices: content.choices || [] };
            });

            finalScenario = {
                title: challenge.title,
                initialSceneId: firstSceneId,
                scenes: assembledScenes
            };
        } else {
            finalScenario = getDefaultScenario();
        }

        setScenario(finalScenario);
        const startID = finalScenario?.initialSceneId;
        if (finalScenario && finalScenario.scenes[startID]) {
            setCurrentScene(finalScenario.scenes[startID]);
        }
        setHistory([]);
        setIsComplete(false);
    }, [challenge]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [history, view]);

    const handleChatSelect = () => {
        setView('chat');
        if (history.length === 0 && currentScene) {
            // Delay the first message slightly for realism
            setTimeout(() => {
                setHistory([{ type: 'attacker', speaker: currentScene.speaker, line: currentScene.line }]);
            }, 500);
        }
    };

    const handleChoice = (choice) => {
        if (isComplete) return;

        setHistory(h => [...h, { type: 'user', speaker: 'You', line: choice.text }]);

        const nextScene = scenario.scenes[choice.nextSceneId];
        if (nextScene) {
            setTimeout(() => {
                // If it's an end state, DO NOT add to history, show popup instead
                if (nextScene.isEndState) {
                    setCurrentScene(nextScene); // Still update current scene for context if needed
                    const feedback = {
                        isCorrect: nextScene.isCorrect,
                        explanation: nextScene.explanation,
                        indicators: nextScene.indicators || []
                    };
                    setFinalFeedback(feedback);
                    setShowFeedbackModal(true);
                } else {
                    // Normal flow
                    setHistory(h => [...h, { type: 'attacker', speaker: nextScene.speaker, line: nextScene.line }]);
                    setCurrentScene(nextScene);
                }
            }, 1200); // Realistic typing delay
        }
    };

    const handleCloseFeedbackModal = () => {
        setShowFeedbackModal(false);
        setIsComplete(true);
        if (onComplete && currentScene) {
            onComplete({
                score: currentScene.isCorrect ? 100 : 0,
                passed: currentScene.isCorrect,
                decisions: { final: "Simulation Ended" }
            });
        }
    };

    const handleBack = () => {
        setView('dmlist');
    };

    const getAttackerAvatar = () => {
        const speaker = currentScene?.speaker || scenario?.scenes?.[scenario?.initialSceneId]?.speaker;
        return SPEAKER_AVATARS[speaker] || SPEAKER_AVATARS['default'];
    };

    // --- SUB-COMPONENTS ---

    const StatusBar = () => (
        <div className="flex justify-between items-center px-3 sm:px-5 py-2 text-white text-xs font-semibold bg-black sticky top-0 z-50">
            <span>{currentTime}</span>
            <div className="flex items-center gap-1.5">
                <Signal size={14} />
                <Wifi size={14} />
                <Battery size={16} />
            </div>
        </div>
    );

    const BottomNav = () => (
        <div className="h-14 bg-black border-t border-gray-800 flex items-center justify-around text-white px-2 z-10 pb-2">
            <Home size={26} onClick={() => setView('feed')} strokeWidth={view === 'feed' ? 3 : 2} className="cursor-pointer" />
            <Search size={26} strokeWidth={2} className="text-gray-400" />
            <PlusSquare size={26} strokeWidth={2} className="text-gray-400" />
            <Clapperboard size={26} strokeWidth={2} className="text-gray-400" />
            <div className={`w-7 h-7 rounded-full bg-gray-600 overflow-hidden border-2 ${view === 'profile' ? 'border-white' : 'border-transparent'}`}>
                <img src={REALISTIC_USERS.me.avatar} alt="Me" className="w-full h-full object-cover" />
            </div>
        </div>
    );

    // --- VIEW RENDERERS ---

    const renderFeed = () => (
        <div className="flex flex-col h-full bg-black text-white font-sans">
            <div className="flex-1 overflow-y-auto scrollbar-hide">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-1 bg-black sticky top-0 z-10">
                    <div className="text-2xl font-bold italic tracking-tighter" style={{ fontFamily: 'system-ui' }}>Instagram</div>
                    <div className="flex items-center gap-5">
                        <Heart size={26} strokeWidth={1.5} />
                        <div className="relative cursor-pointer transition-transform active:scale-95" onClick={() => setView('dmlist')}>
                            <MessageCircle size={26} strokeWidth={1.5} />
                            <div className="absolute -top-1.5 -right-1 min-w-[18px] h-[18px] bg-[#FF3040] rounded-full text-[11px] flex items-center justify-center font-bold px-1 border-2 border-black">1</div>
                        </div>
                    </div>
                </div>

                {/* Stories */}
                <div className="flex gap-4 overflow-x-auto px-4 py-2 border-b border-gray-900 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-3">
                    <div className="flex flex-col items-center gap-1.5 min-w-[72px] cursor-pointer">
                        <div className="w-[72px] h-[72px] rounded-full flex items-center justify-center relative">
                            <div className="w-full h-full rounded-full overflow-hidden border border-gray-800">
                                <img src={REALISTIC_USERS.me.avatar} alt="Me" className="w-full h-full object-cover opacity-90" />
                            </div>
                            <div className="absolute bottom-0 right-0 bg-blue-500 rounded-full p-0.5 border-2 border-black">
                                <PlusSquare size={14} className="text-white bg-blue-500" />
                            </div>
                        </div>
                        <span className="text-[11px] text-gray-400">Your story</span>
                    </div>
                    {REALISTIC_USERS.stories.map((story, i) => (
                        <div key={i} className="flex flex-col items-center gap-1.5 min-w-[72px] cursor-pointer">
                            <div className="w-[72px] h-[72px] rounded-full p-[2px] bg-gradient-to-tr from-[#FFD600] via-[#FF0169] to-[#D300C5]">
                                <div className="w-full h-full rounded-full bg-black p-[2px]">
                                    <img src={story.avatar} alt="User" className="w-full h-full rounded-full object-cover" />
                                </div>
                            </div>
                            <span className="text-[11px] text-white truncate w-16 text-center">{story.username}</span>
                        </div>
                    ))}
                </div>

                {/* Post */}
                {REALISTIC_USERS.feed.map((post, i) => (
                    <div key={i} className="flex flex-col pb-4 border-b border-gray-900">
                        <div className="flex items-center justify-between p-3">
                            <div className="flex items-center gap-2.5">
                                <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-800">
                                    <img src={post.avatar} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="font-semibold text-sm leading-none">{post.username}</span>
                                    <span className="text-xs text-white leading-none mt-1">{post.location}</span>
                                </div>
                            </div>
                            <MoreHorizontal size={20} className="text-white" />
                        </div>

                        <div className="w-full aspect-square bg-gray-900 relative">
                            <img src={post.image} alt="Post" className="w-full h-full object-cover" />
                        </div>

                        <div className="px-3 pt-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-4">
                                    <Heart size={26} strokeWidth={1.5} className="hover:text-gray-400 cursor-pointer" />
                                    <MessageCircle size={26} strokeWidth={1.5} className="hover:text-gray-400 cursor-pointer -rotate-90" />
                                    <Share2 size={26} strokeWidth={1.5} className="hover:text-gray-400 cursor-pointer" />
                                </div>
                                <Bookmark size={26} strokeWidth={1.5} className="hover:text-gray-400 cursor-pointer" />
                            </div>
                            <div className="font-semibold text-sm mb-1">{post.likes} likes</div>
                            <div className="text-sm leading-snug">
                                <span className="font-semibold mr-2">{post.username}</span>
                                {post.caption}
                            </div>
                            <div className="text-gray-500 text-[10px] mt-2 uppercase tracking-wide">{post.time}</div>
                        </div>
                    </div>
                ))}
            </div>
            <BottomNav />
        </div>
    );

    const renderDMList = () => (
        <div className="flex flex-col h-full bg-black text-white font-sans">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 pb-2">
                <div className="flex items-center gap-2 cursor-pointer">
                    <ChevronLeft size={28} onClick={() => setView('feed')} />
                    <div className="flex items-center gap-1">
                        <span className="text-xl font-bold">{REALISTIC_USERS.me.username}</span>
                        <div className="bg-red-500 w-2.5 h-2.5 rounded-full" />
                    </div>
                </div>
                <div className="flex gap-4">
                    <Video size={26} strokeWidth={1.5} />
                    <Edit size={26} strokeWidth={1.5} />
                </div>
            </div>

            {/* Search */}
            <div className="px-4 py-3">
                <div className="bg-[#262626] rounded-xl h-9 flex items-center px-3 gap-3">
                    <Search size={16} className="text-gray-400" />
                    <span className="text-gray-400 text-sm font-light">Search</span>
                </div>
            </div>

            {/* Notes */}
            <div className="flex gap-4 overflow-x-auto px-4 py-2 scrollbar-hide pb-4">
                <div className="flex flex-col items-center gap-1 min-w-[80px]">
                    <div className="w-[80px] h-[80px] relative">
                        <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                            <div className="bg-[#262626] rounded-2xl px-3 py-1.5 text-[11px] text-white text-center shadow-lg truncate max-w-[80px]">
                                Bored 😴
                            </div>
                        </div>
                        <div className="w-full h-full rounded-full bg-gray-800 flex items-center justify-center">
                            <img src={REALISTIC_USERS.me.avatar} alt="Me" className="w-[76px] h-[76px] rounded-full object-cover opacity-80" />
                        </div>
                    </div>
                    <span className="text-[11px] text-gray-400 mt-1">Your note</span>
                </div>
                {/* Random Notes */}
                {[
                    { u: REALISTIC_USERS.stories[0], n: 'Gym time! 💪' },
                    { u: REALISTIC_USERS.stories[2], n: 'New post ✨' }
                ].map((item, i) => (
                    <div key={i} className="flex flex-col items-center gap-1 min-w-[80px]">
                        <div className="w-[80px] h-[80px] relative">
                            <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                                <div className="bg-[#262626] rounded-2xl px-3 py-1.5 text-[11px] text-white text-center shadow-lg truncate max-w-[80px]">
                                    {item.n}
                                </div>
                            </div>
                            <div className="w-full h-full rounded-full border-2 border-green-500 p-[2px]">
                                <img src={item.u.avatar} alt="User" className="w-full h-full rounded-full object-cover" />
                            </div>
                        </div>
                        <span className="text-[11px] text-white mt-1">{item.u.username}</span>
                    </div>
                ))}
            </div>

            <div className="flex justify-between px-4 py-2 mt-2">
                <span className="font-bold text-base">Messages</span>
                <span className="text-[#0095F6] text-sm font-semibold">Requests</span>
            </div>

            <div className="flex-1 overflow-y-auto">
                {/* ATTACKER CHAT */}
                <div
                    onClick={handleChatSelect}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-[#121212] cursor-pointer transition-colors active:bg-[#262626]"
                >
                    <div className="relative">
                        <div className="w-14 h-14 rounded-full bg-gray-800 border border-gray-700 p-0.5 overflow-hidden">
                            <img src={getAttackerAvatar()} alt="Attacker" className="w-full h-full rounded-full object-cover" />
                        </div>
                        {/* New Message Indicator */}
                        <div className="absolute top-0 right-0 w-3.5 h-3.5 bg-[#0095F6] border-2 border-black rounded-full"></div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                            <span className="text-white font-medium text-sm truncate flex items-center gap-1">
                                {currentScene?.speaker || 'Unknown'}
                                {/* Fake Verified Badge */}
                                <svg aria-label="Verified" className="fill-[#0095F6]" height="12" role="img" viewBox="0 0 40 40" width="12">
                                    <path d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z" fillRule="evenodd"></path>
                                </svg>
                            </span>
                        </div>
                        <div className="flex justify-between items-center mt-0.5">
                            <span className={`text-sm truncate max-w-[200px] ${history.length > 0 ? 'text-gray-400 font-normal' : 'text-white font-bold'}`}>
                                {history.length > 0 ? history[history.length - 1].line : "Sent you a message"}
                            </span>
                            <span className="text-gray-400 text-xs flex-shrink-0">· 1m</span>
                        </div>
                    </div>
                    <Camera size={24} className="text-gray-400 flex-shrink-0" />
                </div>

                {/* OTHER REALISTIC CHATS */}
                {REALISTIC_USERS.dms.map((chat, idx) => (
                    <div key={idx} className="flex items-center gap-3 px-4 py-2 hover:bg-[#121212] cursor-pointer active:bg-[#262626]">
                        <div className="w-14 h-14 rounded-full bg-gray-800 overflow-hidden border border-gray-800">
                            <img src={chat.avatar} alt={chat.username} className="w-full h-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-white font-medium text-sm truncate">{chat.username}</div>
                            <div className={`text-sm truncate ${chat.unread ? 'text-white font-bold' : 'text-gray-400'}`}>
                                {chat.msg} <span className="text-xs text-gray-500">· {chat.time}</span>
                            </div>
                        </div>
                        <Camera size={24} className="text-gray-400 flex-shrink-0" />
                    </div>
                ))}
            </div>
            <BottomNav />
        </div>
    );

    const renderChat = () => (
        <div className="flex flex-col h-full bg-black text-white font-sans">
            {/* Chat Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-[#262626] bg-black z-20 shrink-0">
                <div className="flex items-center gap-4">
                    <ChevronLeft size={30} onClick={handleBack} className="cursor-pointer -ml-2" />
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-gray-700 overflow-hidden border border-gray-600">
                            <img src={getAttackerAvatar()} alt="User" className="w-full h-full object-cover" />
                        </div>
                        <div>
                            <div className="font-semibold text-sm flex items-center gap-1">
                                {currentScene?.speaker || 'Unknown'}
                                <svg aria-label="Verified" className="fill-[#0095F6]" height="10" role="img" viewBox="0 0 40 40" width="10">
                                    <path d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z" fillRule="evenodd"></path>
                                </svg>
                            </div>
                            <div className="text-xs text-gray-400">Active now</div>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-5 text-white">
                    <Phone size={24} />
                    <Video size={28} />
                    <Info size={24} />
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto px-4 py-2 pb-20">
                {/* Profile Context */}
                <div className="flex flex-col items-center mt-6 mb-8 text-center">
                    <div className="w-20 h-20 rounded-full overflow-hidden mb-3 border border-gray-700">
                        <img src={getAttackerAvatar()} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="text-lg font-bold flex items-center gap-1">
                        {currentScene?.speaker || 'Unknown'}
                        <svg aria-label="Verified" className="fill-[#0095F6]" height="16" role="img" viewBox="0 0 40 40" width="16">
                            <path d="M19.998 3.094 14.638 0l-2.972 5.15H5.432v6.354L0 14.64 3.094 20 0 25.359l5.432 3.137v5.905h5.975L14.638 40l5.36-3.094L25.358 40l3.232-5.6h6.162v-6.01L40 25.359 36.905 20 40 14.641l-5.248-3.03v-6.46h-6.419L25.358 0l-5.36 3.094Zm7.415 11.225 2.254 2.287-11.43 11.5-6.835-6.93 2.244-2.258 4.587 4.581 9.18-9.18Z" fillRule="evenodd"></path>
                        </svg>
                    </h3>
                    <p className="text-sm text-gray-400">{currentScene?.speaker || 'Unknown'} · Instagram</p>
                    <p className="text-xs text-gray-500 mt-1">452K followers · 0 posts</p>
                    <button className="bg-[#262626] text-white text-sm font-semibold px-4 py-1.5 rounded-lg mt-4">View Profile</button>
                </div>

                <div className="text-center text-[10px] text-gray-500 my-6 font-medium">
                    {new Date().toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}, {currentTime}
                </div>

                {history.map((msg, idx) => {
                    const isUser = msg.type === 'user';
                    return (
                        <div key={idx} className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
                            {!isUser && (
                                <div className="w-7 h-7 rounded-full bg-gray-700 overflow-hidden mr-2 self-end mb-1">
                                    <img src={getAttackerAvatar()} className="w-full h-full object-cover" />
                                </div>
                            )}
                            <div className={`max-w-[75%] px-4 py-2.5 text-[15px] leading-snug whitespace-pre-wrap ${isUser
                                ? 'bg-[#3797F0] text-white rounded-[20px] rounded-br-md'
                                : 'bg-[#262626] text-white rounded-[20px] rounded-bl-md'
                                }`}>
                                {msg.line}
                            </div>
                        </div>
                    )
                })}

                {/* Choices (Suggested Actions) */}
                {!isComplete && currentScene?.choices && (
                    <div className="flex flex-col items-end gap-2 mt-6">
                        {currentScene.choices.map((choice, idx) => (
                            <button
                                key={idx}
                                onClick={() => handleChoice(choice)}
                                className="bg-transparent border border-gray-600 text-white py-2 px-5 rounded-full text-sm font-semibold hover:bg-[#262626] transition-colors active:scale-95"
                            >
                                {choice.text}
                            </button>
                        ))}
                    </div>
                )}

                <div ref={chatEndRef} className="h-4"></div>
            </div>

            {/* Input Area */}
            <div className="p-3 flex items-center gap-3 bg-black border-t border-[#262626] shrink-0">
                <div className="w-9 h-9 bg-[#262626] rounded-full flex items-center justify-center text-white cursor-pointer hover:bg-gray-800">
                    <Camera size={22} strokeWidth={2} />
                </div>
                <div className="flex-1 bg-[#262626] rounded-full h-11 flex items-center px-4 gap-3 transition-all border border-transparent focus-within:border-gray-700">
                    <input
                        type="text"
                        placeholder="Message..."
                        className="bg-transparent text-white text-[15px] w-full focus:outline-none placeholder-gray-400"
                        disabled
                    />
                    <Mic size={20} className="text-white cursor-pointer" />
                    <ImageIcon size={20} className="text-white cursor-pointer" />
                    <div className="text-white cursor-pointer hover:scale-110 transition-transform">
                        <svg aria-label="Sticker" fill="currentColor" height="20" role="img" viewBox="0 0 24 24" width="20"><path d="M12.001.504a11.5 11.5 0 1 0 11.5 11.5 11.513 11.513 0 0 0-11.5-11.5Zm4.363 6.94c.837 0 1.515.748 1.515 1.67a1.6 1.6 0 0 1-1.515 1.669 1.599 1.599 0 0 1-1.514-1.669c0-.922.677-1.67 1.514-1.67Zm-8.726 0c.837 0 1.515.748 1.515 1.67a1.6 1.6 0 0 1-1.515 1.669 1.599 1.599 0 0 1-1.515-1.669c0-.922.678-1.67 1.515-1.67Zm4.363 12.032a5.72 5.72 0 0 1-4.729-2.559.75.75 0 0 1 1.258-.881 4.218 4.218 0 0 0 6.942 0 .75.75 0 0 1 1.258.881 5.72 5.72 0 0 1-4.729 2.559Z"></path></svg>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderInitialPopup = () => {
        if (!showInitialPopup) return null;

        return (
            <div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-60 p-6">
                <div className="bg-[#1e1e1e] rounded-2xl shadow-2xl max-w-sm w-full p-8 text-center border border-gray-800">
                    <div className="text-white space-y-6">
                        <div className="text-2xl font-bold">Information</div>
                        <div className="text-gray-300 text-base leading-relaxed whitespace-pre-wrap">
                            You have to assume that this is your instagram account. However, none of the other features except your Direct Message (DM) are available. You can go to your DMs to view the message and start the challenge.
                        </div>
                    </div>
                    <button
                        onClick={handleCloseInitialPopup}
                        className="mt-8 w-full py-3 bg-[#0095F6] hover:bg-[#0081d6] text-white font-bold rounded-xl transition-colors"
                    >
                        Begin Simulation
                    </button>
                </div>
            </div>
        );
    };

    const renderFeedbackModal = () => {
        if (!showFeedbackModal || !finalFeedback) return null;
        const { isCorrect, explanation, indicators } = finalFeedback;

        return (
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                <div className="bg-[#262626] rounded-xl shadow-2xl max-w-xs w-full p-6 m-4 border border-gray-700 animate-fade-in-up text-white">
                    <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-3">
                            {isCorrect ? <CheckCircle size={28} className="text-green-500" /> : <AlertTriangle size={28} className="text-red-500" />}
                            <h2 className={`text-xl font-bold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                                {isCorrect ? 'Simulation Passed' : 'Simulation Failed'}
                            </h2>
                        </div>
                        <button onClick={handleCloseFeedbackModal} className="text-gray-400 hover:text-white">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="text-gray-200 space-y-4 text-sm">
                        <p>{explanation}</p>

                        {indicators && indicators.length > 0 && (
                            <div className="mt-4 pt-4 border-t border-gray-700">
                                <h3 className="font-bold text-yellow-500 flex items-center mb-2">
                                    <AlertTriangle size={14} className="mr-2" />
                                    Red Flags:
                                </h3>
                                <ul className="list-disc list-inside space-y-1 text-gray-300 pl-1">
                                    {indicators.map((indicator, index) => <li key={index}>{indicator}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            onClick={handleCloseFeedbackModal}
                            className="w-full py-2.5 text-sm font-semibold text-white bg-[#0095F6] rounded-lg hover:bg-[#0081d6] transition-colors"
                        >
                            Continue
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="relative h-[100dvh] w-full max-w-sm mx-auto bg-black rounded-none sm:rounded-[30px] border-0 sm:border-[6px] border-[#1a1a1a] shadow-2xl overflow-hidden font-sans ring-0 sm:ring-1 ring-gray-800">
            <StatusBar />
            <div className="h-[calc(100dvh-32px)] flex flex-col">
                {view === 'feed' && renderFeed()}
                {view === 'dmlist' && renderDMList()}
                {view === 'chat' && renderChat()}
            </div>
            {renderFeedbackModal()}
            {renderInitialPopup()}
        </div>
    );
};

export default InstagramSimulator;